import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Card,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Close,
  Business,
  CheckCircle,
} from '@mui/icons-material';
import API from '../../API';

const InvoiceSettingsDialog = ({ 
  open, 
  onClose, 
  onSettingsUpdated,
  onUploadLogo,
}) => {
  const [form, setForm] = useState({
    invoice_name: '',
    logo_url: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone_number: '',
    email: '',
    website: '',
    tax_id: '',
    default_notes: '',
    default_terms: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [existingSettings, setExistingSettings] = useState(null);

  // Fetch settings when dialog opens
  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    setIsFetching(true);
    try {
      const response = await API.invoiceSettings.get();
      if (response.settings) {
        setExistingSettings(response.settings);
        setForm(response.settings);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching invoice settings:", error);
      }
      // 404 is fine - means no settings exist yet
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = existingSettings 
        ? await API.invoiceSettings.update(form)
        : await API.invoiceSettings.create(form);
      
      if (response.settings) {
        onSettingsUpdated?.(response.settings);
        onClose();
      }
    } catch (error) {
      console.error("Error saving invoice settings:", error);
      alert(`Error saving settings: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveLogo = () => {
    handleChange('logo_url', '');
  };

  const handleClose = () => {
    // Reset form on close
    if (existingSettings) {
      setForm(existingSettings);
    } else {
      setForm({
        invoice_name: '',
        logo_url: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'US',
        phone_number: '',
        email: '',
        website: '',
        tax_id: '',
        default_notes: '',
        default_terms: '',
      });
    }
    onClose();
  };

  const isValid = form.invoice_name?.trim().length > 0;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Business color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {existingSettings ? 'Edit Invoice Settings' : 'Create Invoice Settings'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customize how your invoices appear to clients
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading settings...
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Company Name */}
            <Grid item xs={12}>
              <TextField
                label="Company/Invoice Name"
                value={form.invoice_name}
                onChange={(e) => handleChange('invoice_name', e.target.value)}
                fullWidth
                required
                placeholder="Your Company LLC"
                helperText="This name will appear on all your invoices"
              />
            </Grid>

            {/* Logo Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Company Logo (Optional)
              </Typography>
              {form.logo_url ? (
                <Card variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box
                      component="img"
                      src={form.logo_url}
                      alt="Company Logo"
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: 'contain',
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'divider',
                        bgcolor: 'grey.50',
                        p: 1,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Logo uploaded successfully
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<CloudUpload />}
                          onClick={onUploadLogo}
                          variant="outlined"
                        >
                          Replace
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Delete />}
                          onClick={handleRemoveLogo}
                          color="error"
                          variant="outlined"
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={onUploadLogo}
                  fullWidth
                  sx={{ 
                    py: 3,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderRadius: 2,
                    '&:hover': {
                      borderStyle: 'dashed',
                      borderWidth: 2,
                    }
                  }}
                >
                  Upload Company Logo
                </Button>
              )}
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Contact Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                fullWidth
                placeholder="contact@company.com"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                value={form.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                fullWidth
                placeholder="+1 (555) 123-4567"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Website"
                value={form.website}
                onChange={(e) => handleChange('website', e.target.value)}
                fullWidth
                placeholder="https://yourcompany.com"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Business Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address Line 1"
                value={form.address_line_1}
                onChange={(e) => handleChange('address_line_1', e.target.value)}
                fullWidth
                placeholder="123 Main Street"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address Line 2"
                value={form.address_line_2}
                onChange={(e) => handleChange('address_line_2', e.target.value)}
                fullWidth
                placeholder="Suite 456"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
                fullWidth
                placeholder="New York"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="State/Province"
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
                fullWidth
                placeholder="NY"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Postal Code"
                value={form.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                fullWidth
                placeholder="10001"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Country"
                value={form.country}
                onChange={(e) => handleChange('country', e.target.value)}
                fullWidth
                placeholder="United States"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Tax ID (Optional)"
                value={form.tax_id}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                fullWidth
                placeholder="XX-XXXXXXX"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Default Terms */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Default Terms & Notes
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Default Invoice Notes"
                value={form.default_notes}
                onChange={(e) => handleChange('default_notes', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Thank you for your business..."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Default Payment Terms"
                value={form.default_terms}
                onChange={(e) => handleChange('default_terms', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Payment due within 30 days..."
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        py: 2.5, 
        borderTop: 1, 
        borderColor: 'divider',
        gap: 1
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          size="large"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !isValid || isFetching}
          size="large"
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? 'Saving...' : (existingSettings ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceSettingsDialog;